import { useState } from "react"
import iconFood from "../../styles/img/midiDeal/iconFood.svg"
import iconSnack from "../../styles/img/midiDeal/iconSnack.svg"
import iconDrink from "../../styles/img/midiDeal/iconDrink.svg"
import clsx from "clsx"
import "./MidiDealDrawer.scss"
import { formatPriceWithEuro } from "../../utils/StringUtils";
import { useSelector } from "react-redux";
import pikkoImg from "../../styles/img/Pikko3D.svg";

const tabs = [
    { id: "main", label: "Repas", icon: iconFood, labelPlural: "repas" },
    { id: "snack", label: "Snack", icon: iconSnack, labelPlural: "snacks" },
    { id: "drink", label: "Boisson", icon: iconDrink, labelPlural: "boissons" },
];

const MidiDealHeader = ({ onClose, price }) => {
    const gradientStyle = {
        background: "linear-gradient(107.64deg, #FFDE59 64.66%, #F19F10 100.98%)"
    };

    const priceGradientStyle = {
        background: "linear-gradient(107.64deg, #f3d349ff 64.66%, #F19F10 100.98%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        position: "relative",
        filter: "drop-shadow(1px 2px 0px #C89825)"
    };

    return (
        <div className="flex justify-between items-start mb-4">
            <div>
                <h2 className="geomanistBold text-[24px] mb-3">Midi Deal</h2>
                <div className="flex items-center">
                    {tabs.map((tab, index) => (
                        <span
                            key={tab.id}
                            style={{
                                ...gradientStyle,
                                marginLeft: index > 0 ? -8 : 0,
                                zIndex: 30 - index * 10
                            }}
                            className="w-14 h-14 rounded-full flex items-center justify-center border border-2 border-white"
                        >
                            <img src={tab.icon} alt={tab.label} className="w-6 h-6" />
                        </span>
                    ))}
                    <span className="relative geomanistBold text-[24px] ml-2" style={{ letterSpacing: "2px" }}>
                        <span
                            className="absolute inset-0"
                            style={{ WebkitTextStroke: "3px #C89825", color: "transparent" }}
                        >{formatPriceWithEuro(price)}</span>
                        <span style={priceGradientStyle}>{formatPriceWithEuro(price)}</span>
                    </span>
                </div>
            </div>
            <button
                onClick={onClose}
                className="w-12 h-12 text-[12px] flex items-center justify-center rounded-full bg-gray-100 font-bold text-gray-800 absolute top-6 z-50 right-4"
            >
                ✕
            </button>
        </div>
    );
};

const MidiDealInstructions = ({ price }) => (
    <div className="bg-[#f9efce] rounded-md px-6 mb-6 border border-[#f3d44900] border-t-4 border-t-[#f3c049] py-8">
        <p className="geomanistBold text-[14px] mb-6">Comment ça marche ?</p>
        <p className="geomanistRegular font-semibold text-[14px] leading-6" style={{ letterSpacing: "0.5px" }}>
            Choisi un plat principal parmi la liste, ajoute un snack compatible et enfin ta boisson. Le tout pour {formatPriceWithEuro(price)}!
        </p>
    </div>
);

const MidiDealTabs = ({ activeTab, setActiveTab }) => (
    <div className="flex gap-3 mb-6 sticky -top-[2.2rem] bg-white pt-4 pb-4 -mx-8 px-8 z-10">
        {tabs.map((tab) => (
            <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx("flex items-center justify-center w-full gap-2 p-6 rounded-2xl active:scale-95 border border-2 transition-all", { "border-black bg-white shadow-[0_4px_0_0px_#000000]": activeTab === tab.id, "border-gray-200 bg-white shadow-[0_4px_0_0px_#d1d5db]": activeTab !== tab.id })}
            >
                <img src={tab.icon} alt={tab.label} className="w-6 h-6" />
                <span className="geomanistBold text-[14px]">{tab.label}</span>
            </button>
        ))}
    </div>
);

const MidiDealProductList = ({ currentProducts, currentTab, category, count = "0" }) => (
    <>
        <p className="geomanistRegular font-semibold text-[14px] mb-4 mt-12">
            {count} {currentTab?.labelPlural} éligibles
        </p>
        <div key={currentTab?.id} className="flex flex-col gap-3 rounded-xl overflow-y-hidden">
            {(currentProducts ?? []).map((midiDealProduct, index) => (
                <div
                    key={midiDealProduct.id}
                    className="flex items-center gap-4 p-6 bg-[#F4F4EF]"
                    style={{
                        animation: `midiDealItemIn 0.3s ease-out ${index * 50}ms both`,
                    }}
                >
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        <img src={midiDealProduct.product.image} alt={midiDealProduct.product.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex flex-col gap-4">
                        <p className="geomanistBold text-[14px]">{midiDealProduct.product.name}</p>
                        <p className="geomanistRegular text-[13px] text-gray-500">{category}</p>
                    </div>
                </div>
            ))}
        </div>
    </>
);

const MidiDealProductListError = () => (
    <div className="flex flex-col items-center justify-center py-12 px-4">
        <img src={pikkoImg} alt="Pikko" className="w-24 h-24 mb-6" />
        <p className="geomanistBold text-[16px] text-center mb-2">
            Oups, on a du mal !
        </p>
        <p className="geomanistRegular text-[14px] text-gray-500 text-center">
            Les produits n'ont pas pu être chargés. Réessaie dans quelques instants.
        </p>
    </div>
);

const MidiDealProductListSkeleton = () => (
    <>
        <div className="bg-gray-200 rounded-lg animate-pulse w-36 h-4 mt-12 mb-4" />
        <div className="flex flex-col gap-3 rounded-xl">
            {[0, 1, 2, 3].map((i) => (
                <div
                    key={i}
                    className="flex items-center gap-4 p-6 bg-[#F4F4EF] rounded-xl"
                    style={{ animation: `midiDealItemIn 0.3s ease-out ${i * 50}ms both` }}
                >
                    <div className="w-20 h-20 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="flex flex-col gap-4">
                        <div className="bg-gray-200 rounded-md animate-pulse w-32 h-4" />
                        <div className="bg-gray-200 rounded-md animate-pulse w-20 h-3" />
                    </div>
                </div>
            ))}
        </div>
    </>
);

export const MidiDealDrawer = ({ isOpen, onClose, products = {}, loading, error }) => {
    const [activeTab, setActiveTab] = useState("main");
    const currentCategory = products?.[activeTab];
    const currentTab = tabs.find(t => t.id === activeTab);
    const { store } = useSelector((state) => state.store);

    const categoryMapper = {
        "drink": "Boisson",
        "main": "Repas",
        "snack": "Snack",
    }

    return (
        <>
            {/* Backdrop */}
            <div
                role="button"
                tabIndex={0}
                className={clsx("fixed inset-0 bg-black bg-opacity-50 z-[1900] transition-opacity", { "opacity-0 pointer-events-none": !isOpen })}
                onClick={onClose}
            />

            {/* Drawer */}
            <div
                className={clsx("fixed bottom-0 h-[85dvh] left-0 right-0 bg-white rounded-t-3xl z-[1999] transition-transform duration-300 ease-out", { "translate-y-0": isOpen, "translate-y-full": !isOpen })}
            >
                <div className="p-8 mt-4 overflow-y-auto" style={{ maxHeight: "85dvh" }}>
                    <MidiDealHeader onClose={onClose} price={store?.midiDeal?.dealPrice} />
                    <MidiDealInstructions price={store?.midiDeal?.dealPrice} />
                    <MidiDealTabs activeTab={activeTab} setActiveTab={setActiveTab} />
                    {error ? (
                        <MidiDealProductListError />
                    ) : loading ? (
                        <MidiDealProductListSkeleton />
                    ) : currentCategory && (
                        <MidiDealProductList
                            currentProducts={currentCategory.products}
                            currentTab={currentTab}
                            category={categoryMapper[activeTab]}
                            count={currentCategory.count}
                        />
                    )}
                </div>
            </div>
        </>
    );
};
